import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AlertTriangle, Archive, Brain, Calendar, Check, CheckSquare, ChevronDown, ChevronLeft,
    Clock, Eye, Forward, GripVertical, Info, KeyRound, LayoutGrid, Mail, MailWarning, MoreHorizontal,
    Reply, ReplyAll, RotateCw, Search, Shield, Trash2, Users,
} from 'lucide-react';
import data from '../../../../data/mcsa/games/catch-the-phish.json';

interface CatchThePhishProps {
    onComplete: () => void;
}

interface INist {
    cueCategory: string;
    premiseCategory: string;
    detectionDifficulty: string;
    canadianRelevance: string;
    audienceType: string[];
    premiseScores: Record<string, number>;
}

interface IEmail {
    id: string;
    sender: string;
    from?: string;
    email?: string;
    subject: string;
    snippet: string;
    body?: string;
    isPhishing: boolean;
    difficulty?: number;
    tags?: string[];
    isInternal: boolean;
    timestamp: string;
    nist?: INist;
    feedback?: string;
}

interface ITicket { code: string; options: string[]; }

type TScreen = 'intro' | 'tutorial' | 'wml' | 'game' | 'recall' | 'transition' | 'complete';
type TTutStep = 'ticket' | 'open_email' | 'report_email' | 'practice_intro' | 'practice' | 'recall' | 'recall_feedback';

const LEVELS = data.levels as { id: number; name: string; wmlSeconds: number; timeLimit: number }[];
const TICKETS = data.tickets as Record<string, ITicket[]>;
const EMAILS = data.emails as IEmail[];
const PRACTICE = data.practice as IEmail[];
const TUTORIAL = data.tutorial as { ticket: string; options: string[] };

const shuffle = <T,>(arr: T[]) => [...arr].sort(() => Math.random() - 0.5);

const timePenalty = (level: number, elapsed: number) => {
    const limit = LEVELS.find((l) => l.id === level)?.timeLimit || 90;
    if (elapsed <= limit) return 0;
    return 1 + Math.floor((elapsed - limit) / 30);
};

const relevanceScore = (email: IEmail) => {
    const scores = email.nist?.premiseScores || {};
    let score = Object.values(scores).reduce((s, v) => s + v, 0);
    if (email.nist?.canadianRelevance === 'high') score += 15;
    else if (email.nist?.canadianRelevance === 'medium') score += 5;
    else score -= 10;
    return Math.max(0, score);
};

const filterPhish = (emails: IEmail[], level: number) => emails.filter((email) => {
    const n = email.nist;
    if (!n) return true;
    if (level === 1) return n.detectionDifficulty === 'least' || n.cueCategory === 'many' || n.premiseCategory === 'weak';
    if (level === 2) return n.premiseCategory === 'medium' || (n.premiseCategory === 'strong' && ((n.audienceType || []).some((a) => ['finance', 'hr'].includes(a)) || (email.tags || []).includes('authority') || (email.tags || []).includes('workflow')));
    return n.detectionDifficulty === 'very' || n.cueCategory === 'few' || (n.premiseCategory === 'strong' && n.canadianRelevance === 'high');
});

const generateLevelEmails = (level: number, avoid: string[]) => {
    const available = EMAILS.filter((e) => !avoid.includes(e.id));
    const phish = available.filter((e) => e.isPhishing);
    const safe = available.filter((e) => !e.isPhishing);
    const scoredP = [...(phish.length ? phish : EMAILS.filter((e) => e.isPhishing))].sort((a, b) => relevanceScore(b) - relevanceScore(a));
    const scoredS = [...(safe.length ? safe : EMAILS.filter((e) => !e.isPhishing))].sort((a, b) => relevanceScore(b) - relevanceScore(a));
    const pick = (pool: IEmail[], extra: IEmail[], count: number, pred: (e: IEmail) => boolean) => {
        const selected: IEmail[] = [];
        const seen = new Set<string>();
        for (const group of [pool.filter(pred), pool, extra.filter(pred), extra]) {
            for (const email of group) {
                if (seen.has(email.id)) continue;
                seen.add(email.id);
                selected.push(email);
                if (selected.length === count) return selected;
            }
        }
        return selected;
    };
    const selectedP = pick(scoredP, EMAILS.filter((e) => e.isPhishing), 4, (e) => filterPhish([e], level).length > 0);
    const selectedS = pick(scoredS, EMAILS.filter((e) => !e.isPhishing), 4, (e) => (level === 1 ? (e.difficulty || 1) <= 2 : level === 2 ? (e.difficulty || 1) <= 3 : true));
    return shuffle([...selectedP, ...selectedS]);
};

const addr = (e: IEmail) => e.from || e.email || '';
const avatarColor = (name: string, isInternal: boolean) => {
    const colors = isInternal ? ['#1a73e8', '#0f9d58', '#e37400', '#a142f4', '#00897b'] : ['#c5221f', '#e37400', '#1557b0', '#188038', '#7627bb'];
    return colors[(name.charCodeAt(0) || 0) % colors.length];
};

const AFFIRMATIONS = [
    "That inbox didn't stand a chance! Your phishing radar is getting sharp.",
    "You kept your cool under the clock and caught the fakes. That's the real skill.",
    "Every phish you spotted is one your future self won't fall for. Nice work.",
    'Sharp eyes, steady nerves — you handled that like a pro.',
];

export const CatchThePhish = ({ onComplete }: CatchThePhishProps) => {
    const [screen, setScreen] = useState<TScreen>('intro');
    const [introPage, setIntroPage] = useState(1);
    const [tutStep, setTutStep] = useState<TTutStep>('ticket');
    const [level, setLevel] = useState(1);
    const [emails, setEmails] = useState<IEmail[]>([]);
    const [viewEmail, setViewEmail] = useState<IEmail | null>(null);
    const [practiceInbox, setPracticeInbox] = useState<IEmail[]>(PRACTICE.filter((e) => e.id !== 'guided-paypal'));
    const [ticket, setTicket] = useState<ITicket | null>(null);
    const [wmlLeft, setWmlLeft] = useState(5);
    const [timeElapsed, setTimeElapsed] = useState(0);
    const [practiceTime, setPracticeTime] = useState(30);
    const [phishFound, setPhishFound] = useState(0);
    const [score, setScore] = useState(0);
    const [history, setHistory] = useState<{ isCorrect: boolean }[]>([]);
    const [recentIds, setRecentIds] = useState<string[]>([]);
    const [showConfidence, setShowConfidence] = useState(false);
    const [confidence, setConfidence] = useState<number | null>(null);
    const [distraction, setDistraction] = useState(false);
    const [feedback, setFeedback] = useState<{ tone: 'idle' | 'correct' | 'wrong'; message: string }>({ tone: 'idle', message: '' });
    const [recallChoice, setRecallChoice] = useState<string | null>(null);
    const [coach, setCoach] = useState('');

    const guided = PRACTICE.find((e) => e.id === 'guided-paypal')!;
    const levelMeta = LEVELS.find((l) => l.id === level)!;

    const mailboxEmails = useMemo(() => {
        if (screen === 'tutorial' && (tutStep === 'practice' || tutStep === 'practice_intro')) return practiceInbox;
        if (screen === 'tutorial') return [guided];
        return emails;
    }, [screen, tutStep, practiceInbox, emails, guided]);

    const beginInbox = useCallback((lvl: number, avoid: string[]) => {
        const next = generateLevelEmails(lvl, avoid);
        setEmails(next);
        setRecentIds([...next.map((e) => e.id), ...avoid].slice(0, 20));
        setTimeElapsed(0);
        setPhishFound(0);
        setViewEmail(null);
        setDistraction(false);
        setShowConfidence(false);
        setConfidence(null);
        setScreen('game');
    }, []);

    const startLevel = useCallback((lvl: number) => {
        const pool = TICKETS[String(lvl)] || TICKETS['1'];
        const picked = pool[Math.floor(Math.random() * pool.length)];
        setTicket({ ...picked, options: shuffle(picked.options) });
        setLevel(lvl);
        setWmlLeft(LEVELS.find((l) => l.id === lvl)?.wmlSeconds || 5);
        setViewEmail(null);
        setShowConfidence(false);
        setConfidence(null);
        setDistraction(false);
        setScreen('wml');
    }, []);

    useEffect(() => {
        if (screen !== 'wml') return;
        const id = setInterval(() => {
            setWmlLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(id);
                    beginInbox(level, recentIds);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [screen, level, beginInbox, recentIds]);

    useEffect(() => {
        if (screen !== 'game' || showConfidence) return;
        const id = setInterval(() => {
            setTimeElapsed((t) => {
                const next = t + 1;
                if (level === 3 && next === 20) setDistraction(true);
                return next;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [screen, showConfidence, level]);

    useEffect(() => {
        if (tutStep !== 'practice' && tutStep !== 'practice_intro') return;
        if (tutStep === 'practice_intro') {
            const t = setTimeout(() => setTutStep('practice'), 1500);
            return () => clearTimeout(t);
        }
        const id = setInterval(() => {
            setPracticeTime((p) => {
                if (p <= 1) {
                    clearInterval(id);
                    setViewEmail(null);
                    setTutStep('recall');
                    return 0;
                }
                return p - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [tutStep]);

    const flash = (tone: 'correct' | 'wrong', message: string, next: () => void, ms = 1000) => {
        setFeedback({ tone, message });
        window.setTimeout(() => {
            setFeedback({ tone: 'idle', message: '' });
            next();
        }, ms);
    };

    const openEmail = (id: string) => {
        const email = mailboxEmails.find((e) => e.id === id);
        if (!email) return;
        setViewEmail(email);
        if (screen === 'tutorial' && tutStep === 'open_email' && email.id === guided.id) {
            setTutStep('report_email');
            setCoach('Sender does not match PayPal. Report it.');
        }
    };

    const report = () => {
        if (!viewEmail) return;
        if (screen === 'tutorial') {
            if (tutStep === 'report_email') {
                flash('correct', guided.feedback || 'Correct.', () => {
                    setViewEmail(null);
                    setTutStep('practice_intro');
                    setPracticeTime(30);
                    setCoach('Find both phishing emails before time runs out.');
                });
                return;
            }
            if (tutStep === 'practice') {
                if (!viewEmail.isPhishing) {
                    flash('wrong', viewEmail.feedback || 'This one is safe. Leave it in the inbox.', () => undefined, 950);
                    return;
                }
                const remaining = practiceInbox.filter((e) => e.id !== viewEmail.id);
                const left = remaining.filter((e) => e.isPhishing).length;
                flash('correct', viewEmail.feedback || 'Correct.', () => {
                    setPracticeInbox(remaining);
                    setViewEmail(null);
                    if (left === 0) setTutStep('recall');
                }, 850);
            }
            return;
        }
        if (screen !== 'game' || showConfidence) return;
        const isCorrect = viewEmail.isPhishing;
        setScore((s) => s + (isCorrect ? 100 : -50));
        if (isCorrect) setPhishFound((n) => n + 1);
        const nextEmails = emails.filter((e) => e.id !== viewEmail.id);
        const nextFound = phishFound + (isCorrect ? 1 : 0);
        setEmails(nextEmails);
        setViewEmail(null);
        setHistory((h) => [...h, { isCorrect }]);
        if (nextFound >= 4 || nextEmails.length === 0) {
            window.setTimeout(() => {
                setScore((s) => s - timePenalty(level, timeElapsed));
                setShowConfidence(true);
            }, 500);
        }
    };

    const formatElapsed = (t: number) => `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
    const pen = timePenalty(level, timeElapsed);
    const phishLeft = practiceInbox.filter((e) => e.isPhishing).length;
    const canReport = Boolean(viewEmail) && (screen === 'game' || tutStep === 'report_email' || tutStep === 'practice');
    const coachText = feedback.tone !== 'idle' ? feedback.message : (tutStep === 'open_email' ? 'Open the email.' : tutStep === 'report_email' ? 'Sender does not match PayPal. Report it.' : coach);

    const renderMailbox = () => (
        <div className="mcsa_game__catch_the_phish__mail">
            <header className="mcsa_game__catch_the_phish__mail_header">
                <div className="mcsa_game__catch_the_phish__brand"><Mail size={18} /><span>CyberMail</span></div>
                <div className="mcsa_game__catch_the_phish__search"><Search size={16} /><input placeholder="Search" readOnly /></div>
                {screen === 'game' ? (
                    <div className="mcsa_game__catch_the_phish__chip">{levelMeta.name}</div>
                ) : (
                    <div className={`mcsa_game__catch_the_phish__chip ${practiceTime <= 10 && (tutStep === 'practice' || tutStep === 'practice_intro') ? 'is-urgent' : ''}`}>
                        Practice
                        {(tutStep === 'practice' || tutStep === 'practice_intro') && (
                            <>
                                <Clock size={17} />
                                <span>0:{String(practiceTime).padStart(2, '0')}</span>
                                <span>{phishLeft} phish left</span>
                            </>
                        )}
                    </div>
                )}
                <div className="mcsa_game__catch_the_phish__avatar">A</div>
            </header>
            <div className="mcsa_game__catch_the_phish__mail_body">
                <aside className="mcsa_game__catch_the_phish__rail">
                    <span className="is-on"><Mail size={20} /></span>
                    <Calendar size={20} />
                    <Users size={20} />
                    <LayoutGrid size={20} />
                    <CheckSquare size={20} />
                </aside>
                <div className="mcsa_game__catch_the_phish__workspace">
                    <div className="mcsa_game__catch_the_phish__toolbar">
                        <RotateCw size={16} />
                        <Archive size={16} />
                        <Trash2 size={16} />
                        <Reply size={16} />
                        <ReplyAll size={16} />
                        <Forward size={16} />
                        {canReport && (
                            <button type="button" className={`mcsa_game__catch_the_phish__report ${tutStep === 'report_email' ? 'is-hot' : ''}`} onClick={report}>
                                <Shield size={16} /> Report Phishing
                            </button>
                        )}
                        <div className="mcsa_game__catch_the_phish__grow" />
                        {level === 3 && screen === 'game' && <span className="mcsa_game__catch_the_phish__threat">THREAT LEVEL ELEVATED</span>}
                    </div>
                    <div className="mcsa_game__catch_the_phish__panes">
                        <aside className="mcsa_game__catch_the_phish__folders">
                            <div className="mcsa_game__catch_the_phish__acct"><ChevronDown size={14} /> cybersec@company.com</div>
                            <div className={`mcsa_game__catch_the_phish__folder ${!viewEmail ? 'is-on' : ''}`}>Inbox <span>{mailboxEmails.length}</span></div>
                            <div className="mcsa_game__catch_the_phish__folder">Drafts</div>
                            <div className="mcsa_game__catch_the_phish__folder">Sent Items</div>
                        </aside>
                        <div className="mcsa_game__catch_the_phish__list">
                            <div className="mcsa_game__catch_the_phish__tabs">
                                <span className="is-on">Focused</span>
                                <span>Other</span>
                                <GripVertical size={16} />
                            </div>
                            <div className="mcsa_game__catch_the_phish__today"><ChevronDown size={14} /> Today</div>
                            {mailboxEmails.map((email) => (
                                <button
                                    key={email.id}
                                    type="button"
                                    className={`mcsa_game__catch_the_phish__row ${viewEmail?.id === email.id ? 'is-sel' : ''} ${tutStep === 'open_email' && email.id === guided.id ? 'is-hl' : ''}`}
                                    onClick={() => openEmail(email.id)}
                                >
                                    <span className="mcsa_game__catch_the_phish__av" style={{ background: avatarColor(email.sender, email.isInternal) }}>{email.sender[0]}</span>
                                    <span className="mcsa_game__catch_the_phish__row_body">
                                        <span className="mcsa_game__catch_the_phish__row_top">
                                            <strong>{email.sender}</strong>
                                            <em>{email.timestamp}</em>
                                        </span>
                                        <span className="mcsa_game__catch_the_phish__subj">{email.subject}</span>
                                        <span className="mcsa_game__catch_the_phish__snip">{email.snippet}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                        <div className="mcsa_game__catch_the_phish__reading">
                            {!viewEmail ? (
                                <div className="mcsa_game__catch_the_phish__empty">
                                    <Mail size={48} />
                                    <h3>Select an email to read</h3>
                                    <p>Choose an email from the list to view its contents</p>
                                    {level === 1 && screen === 'game' && (
                                        <p className="mcsa_game__catch_the_phish__tip">Tip: Look for suspicious sender addresses, urgent requests for credentials, and unexpected attachments. Use the “Report Phishing” button in the toolbar to report suspicious emails.</p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="mcsa_game__catch_the_phish__read_bar">
                                        <button type="button" onClick={() => setViewEmail(null)}><ChevronLeft size={16} /></button>
                                        <Archive size={16} /><Trash2 size={16} /><MoreHorizontal size={16} />
                                    </div>
                                    <div className="mcsa_game__catch_the_phish__read_body">
                                        <h1>{viewEmail.subject}</h1>
                                        <div className="mcsa_game__catch_the_phish__from">
                                            <span className="mcsa_game__catch_the_phish__av" style={{ background: avatarColor(viewEmail.sender, viewEmail.isInternal) }}>{viewEmail.sender[0]}</span>
                                            <div>
                                                <strong>{viewEmail.sender}</strong>
                                                <span>&lt;{addr(viewEmail)}&gt;</span>
                                                <em>to me</em>
                                            </div>
                                        </div>
                                        {!viewEmail.isInternal && (
                                            <div className="mcsa_game__catch_the_phish__outside">
                                                <AlertTriangle size={15} />
                                                This message was sent from outside your organization. Be careful with links and attachments.
                                            </div>
                                        )}
                                        <p>{viewEmail.body || viewEmail.snippet}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {screen === 'game' && (
                    <div className={`mcsa_game__catch_the_phish__timer ${pen > 0 ? 'is-bad' : ''}`}>
                        <div><Clock size={14} /> {formatElapsed(timeElapsed)} {pen > 0 && <span>-{pen}</span>}</div>
                        <small>{pen > 0 ? 'Losing points' : 'Time elapsed'}</small>
                    </div>
                )}
                {coachText && screen === 'tutorial' && (
                    <div className={`mcsa_game__catch_the_phish__coach ${feedback.tone}`}>{coachText}</div>
                )}
                {distraction && (
                    <div className="mcsa_game__catch_the_phish__overlay">
                        <div className="mcsa_game__catch_the_phish__dist">
                            <header><span>System Update</span></header>
                            <AlertTriangle size={48} />
                            <h3>Critical Patch Required</h3>
                            <p>System will restart automatically in 15 seconds to install security definitions.</p>
                            <button type="button" onClick={() => setDistraction(false)}>Defer (15m)</button>
                        </div>
                    </div>
                )}
                {showConfidence && (
                    <div className="mcsa_game__catch_the_phish__overlay">
                        <div className="mcsa_game__catch_the_phish__conf">
                            <h3>How confident are you that you found all and ONLY the correct phishing emails?</h3>
                            <div className="mcsa_game__catch_the_phish__dots">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button key={n} type="button" className={confidence === n ? 'is-on' : ''} onClick={() => setConfidence(n)}>{n}</button>
                                ))}
                            </div>
                            <div className="mcsa_game__catch_the_phish__conf_labels"><span>Not confident</span><span>Very confident</span></div>
                            <button
                                type="button"
                                className="mcsa_game__catch_the_phish__cta"
                                disabled={confidence == null}
                                onClick={() => { if (confidence == null) return; setShowConfidence(false); setScreen('recall'); }}
                            >
                                Keep going!
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const glass = (inner: React.ReactNode) => (
        <div className="mcsa_game__catch_the_phish__stage">{inner}</div>
    );

    if (screen === 'intro') {
        return (
            <div className="mcsa_game__catch_the_phish">
                <div className="mcsa_game__catch_the_phish__mod">
                    {introPage === 1 ? (
                        <>
                            <div className="mcsa_game__catch_the_phish__mod_icon"><MailWarning size={28} /></div>
                            <h1>Catch the Phish!</h1>
                            <p>Think you can outsmart a phishing scammer? Step into a live inbox, sniff out the fakes, and report them... <b>all while keeping one detail locked in your memory.</b></p>
                            <div className="mcsa_game__catch_the_phish__meta"><Clock size={16} /> 3 levels · Timed — counts up · Harder each level</div>
                            <div className="mcsa_game__catch_the_phish__briefs">
                                <div><KeyRound size={20} /><span>First, memorize your assigned <strong>Ticket ID</strong>, you’ll be asked for it later.</span></div>
                                <div><Shield size={20} /><span>Hunt down and report every phishing email. Do not report legitimate emails!</span></div>
                                <div><Check size={20} /><span>Finally, recall your Ticket ID to close out the level.</span></div>
                            </div>
                            <button type="button" className="mcsa_game__catch_the_phish__cta" onClick={() => setIntroPage(2)}>Next: how scoring works</button>
                            <button type="button" className="mcsa_game__catch_the_phish__skip" onClick={() => { setScreen('tutorial'); setTutStep('ticket'); }}>Let me warm up first</button>
                        </>
                    ) : (
                        <>
                            <div className="mcsa_game__catch_the_phish__mod_icon"><Info size={28} /></div>
                            <h1>How your score works</h1>
                            <p>Every move counts. Here’s exactly what raises your score and what drags it down.</p>
                            <div className="mcsa_game__catch_the_phish__scores">
                                <div><Check size={18} /> Report a phishing email → you gain points.</div>
                                <div><Shield size={18} /> Recall the correct Ticket ID → bonus points.</div>
                                <div><AlertTriangle size={18} /> Report a real email → you lose points.</div>
                                <div><Clock size={18} /> Each level counts up. The longer you take, the more points slip away.</div>
                            </div>
                            <div className="mcsa_game__catch_the_phish__times">
                                <span>Level 1 · 1:30 grace</span>
                                <span>Level 2 · 2:00 grace</span>
                                <span>Level 3 · 2:30 grace</span>
                            </div>
                            <p className="mcsa_game__catch_the_phish__note"><Eye size={15} /> Every level gets trickier and the fakes get harder to spot.</p>
                            <button type="button" className="mcsa_game__catch_the_phish__cta" onClick={() => { setScreen('tutorial'); setTutStep('ticket'); }}>Show me what you’ve got!</button>
                            <button type="button" className="mcsa_game__catch_the_phish__skip" onClick={() => setIntroPage(1)}>← Back</button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    if (screen === 'tutorial' && tutStep === 'ticket') {
        return glass(
            <div className="mcsa_game__catch_the_phish__glass">
                <div className="mcsa_game__catch_the_phish__pill">Practice</div>
                <h2>Memorize this ticket ID</h2>
                <div className="mcsa_game__catch_the_phish__ticket">{TUTORIAL.ticket}</div>
                <p>You will need it after the practice inbox.</p>
                <button type="button" className="mcsa_game__catch_the_phish__cta" onClick={() => setTutStep('open_email')}>Start Practice</button>
                <button type="button" className="mcsa_game__catch_the_phish__skip" onClick={() => startLevel(1)}>Skip tutorial</button>
            </div>
        );
    }

    if (screen === 'tutorial' && (tutStep === 'recall' || tutStep === 'recall_feedback')) {
        return glass(
            <div className="mcsa_game__catch_the_phish__glass">
                <div className="mcsa_game__catch_the_phish__pill">Practice</div>
                <h2>Which ticket ID did you memorize?</h2>
                <div className="mcsa_game__catch_the_phish__recall">
                    {TUTORIAL.options.map((o) => (
                        <button
                            key={o}
                            type="button"
                            disabled={tutStep === 'recall_feedback'}
                            className={recallChoice === o ? 'is-sel' : ''}
                            onClick={() => {
                                setRecallChoice(o);
                                const ok = o === TUTORIAL.ticket;
                                setTutStep('recall_feedback');
                                flash(ok ? 'correct' : 'wrong', ok ? 'Good. You are ready.' : 'Close. The real round will ask again after the inbox.', () => startLevel(1));
                            }}
                        >
                            {o}
                        </button>
                    ))}
                </div>
                {feedback.message && <div className={`mcsa_game__catch_the_phish__fb ${feedback.tone}`}>{feedback.message}</div>}
            </div>
        );
    }

    if (screen === 'tutorial') return <div className="mcsa_game__catch_the_phish mcsa_game__catch_the_phish--mail">{renderMailbox()}</div>;

    if (screen === 'wml' && ticket) {
        const total = levelMeta.wmlSeconds;
        const pct = ((total - wmlLeft) / total) * 100;
        return glass(
            <div className="mcsa_game__catch_the_phish__glass">
                <div className="mcsa_game__catch_the_phish__pill">Level {level}</div>
                <h2>Remember this ticket ID</h2>
                <div className="mcsa_game__catch_the_phish__ticket">{ticket.code}</div>
                <div className="mcsa_game__catch_the_phish__wml">{wmlLeft}</div>
                <div className="mcsa_game__catch_the_phish__wml_track"><div style={{ width: `${pct}%` }} /></div>
            </div>
        );
    }

    if (screen === 'game') return <div className="mcsa_game__catch_the_phish mcsa_game__catch_the_phish--mail">{renderMailbox()}</div>;

    if (screen === 'recall' && ticket) {
        return glass(
            <div className="mcsa_game__catch_the_phish__glass">
                <div className="mcsa_game__catch_the_phish__pill">Level {level}</div>
                <h2>Which ticket ID were you asked to remember?</h2>
                <div className="mcsa_game__catch_the_phish__recall">
                    {ticket.options.map((o) => (
                        <button
                            key={o}
                            type="button"
                            onClick={() => {
                                setScore((s) => s + (o === ticket.code ? 50 : -25));
                                if (level < 3) setScreen('transition');
                                else setScreen('complete');
                            }}
                        >
                            {o}
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    if (screen === 'transition') {
        const to = level + 1;
        return (
            <div className="mcsa_game__catch_the_phish">
                <div className="mcsa_game__catch_the_phish__mod">
                    <div className="mcsa_game__catch_the_phish__pill">🚀 Level {level} crushed!</div>
                    <h2>Ready to level up to {to}?</h2>
                    <p>New challenges ahead:</p>
                    <div className="mcsa_game__catch_the_phish__next">
                        <div><Clock size={20} /><span>{to === 2 ? '2:00 grace window' : '2:30 grace window'}</span></div>
                        <div><Eye size={20} /><span>Harder to spot</span></div>
                        <div><Brain size={20} /><span>{to === 2 ? 'Longer ticket IDs' : 'Closer ticket matches'}</span></div>
                    </div>
                    <button type="button" className="mcsa_game__catch_the_phish__cta" onClick={() => startLevel(to)}>I’m ready for this!</button>
                </div>
            </div>
        );
    }

    const caught = history.filter((h) => h.isCorrect).length;
    const aff = AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)];
    return (
        <div className="mcsa_game__catch_the_phish">
            <div className="mcsa_game__catch_the_phish__mod">
                <div className="mcsa_game__catch_the_phish__mod_icon"><Shield size={26} /></div>
                <p className="mcsa_game__catch_the_phish__emoji">🏆</p>
                <h2>You crushed it!</h2>
                <p>{aff}</p>
                <div className="mcsa_game__catch_the_phish__result_stats">
                    <div><strong>{score}</strong><span>Points</span></div>
                    <div><strong>{caught}</strong><span>Phish caught</span></div>
                    <div><strong>3</strong><span>Levels cleared</span></div>
                </div>
                <div className="mcsa_game__catch_the_phish__affirm"><Check size={18} /> You just leveled up your instinct for spotting scams. That skill travels with you everywhere.</div>
                <button type="button" className="mcsa_game__catch_the_phish__cta" onClick={onComplete}>Keep the momentum going!</button>
                <button
                    type="button"
                    className="mcsa_game__catch_the_phish__skip"
                    onClick={() => {
                        setScore(0);
                        setHistory([]);
                        setRecentIds([]);
                        setPracticeInbox(PRACTICE.filter((e) => e.id !== 'guided-paypal'));
                        setIntroPage(1);
                        setScreen('intro');
                    }}
                >
                    Play again
                </button>
            </div>
        </div>
    );
};
